import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {  CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { GemIcon } from '../components/GemIcon';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GemSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [gems, setGems] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const checkStatus = async (sessionId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/gems/purchase/status/${sessionId}`, { withCredentials: true });
      if (data.payment_status === 'paid') {
        setStatus('success');
        setGems(data.gems);
      } else if (data.status === 'expired' || data.payment_status === 'unpaid') {
        setStatus('failed');
      } else {
        // Still processing, retry
        setTimeout(() => checkStatus(sessionId), 2000);
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center max-w-sm">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-[#F59E0B] mx-auto mb-4 animate-spin" />
            <h2 className="text-xl font-bold text-white mb-2">Processing Payment...</h2>
            <p className="text-[#71717A] text-sm">Please wait while we confirm your purchase</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2" data-testid="gem-success-title">Gems Added!</h2>
            <div className="flex items-center justify-center gap-2 mb-4">
              <GemIcon size={24} />
              <span className="text-2xl font-bold text-[#F59E0B]" data-testid="gems-received">+{gems.toLocaleString()}</span>
            </div>
            <p className="text-[#71717A] text-sm mb-6">Your gems have been added to your account</p>
            <Button onClick={() => navigate('/profile')} className="bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-full px-8" data-testid="back-to-profile-btn">
              Back to Profile
            </Button>
          </>
        )}
        {(status === 'failed' || status === 'error') && (
          <>
            <div className="w-20 h-20 mx-auto mb-4 bg-[#E11D48]/20 rounded-full flex items-center justify-center">
              <XCircle className="w-10 h-10 text-[#E11D48]" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Payment Failed</h2>
            <p className="text-[#71717A] text-sm mb-6">Something went wrong. No charges were made.</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/gems')} variant="outline" className="border-white/20 text-white rounded-full" data-testid="try-again-btn">
                Try Again
              </Button>
              <Button onClick={() => navigate('/profile')} className="bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-full" data-testid="back-profile-btn">
                Profile
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
